import ContentIllustration from '../../../components/ui/ContentIllustration';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useCustomStore from '../../../store/customStore';
import useAuthStore from '../../../store/authStore';
import useDashboardPrefs from '../../../hooks/useDashboardPrefs';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import api from '../../../services/api';
import { Plus, Edit3, Send, Save, Loader2, Lock, CreditCard, Upload, Share2, Settings, ChevronLeft, ChevronRight } from 'lucide-react';
import ScopeLandingInline from './ScopeLandingInline';
import PublishForm from '../../../components/partner/PublishForm';
import PublicationStatus from '../../../components/partner/PublicationStatus';
import ShareCodesPanel from '../../../components/share/ShareCodesPanel';
import BaseModal from '../../../components/ui/BaseModal';
import SubscriptionModal from '../../../components/stripe/SubscriptionModal';
import useFeaturePing from '../../../hooks/useFeaturePing';
import TrialStartCard from '../../../components/custom/TrialStartCard';

const PRESET_COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#64748B'];

export default function MesThematiquesHub({ hubImage, hubColor, onActiveChange, headerSlot, initialActiveSlug, onActiveSlugChange }) {
    useFeaturePing('mes_thematiques');
    const navigate = useNavigate();
    const { user, dashboardPrefs } = useAuthStore();
    const { prefs, setCustomThematiqueOrder, setTabForScope } = useDashboardPrefs(dashboardPrefs);
    const dragIdxRef = useRef(null);
    const {
        thematiques, sharedThematiques, thematiquesLoading,
        fetchThematiques, createThematique, deleteThematique, updateThematique,
    } = useCustomStore();

    const [pricing, setPricing] = useState(null);
    const [unusedSubs, setUnusedSubs] = useState(0);
    // Essai gratuit (migration 181) : proposé si aucun essai consommé ni thématique à soi.
    const [trialOffer, setTrialOffer] = useState(null); // { nb_questions, days } ou null
    const [unlockMode, setUnlockMode] = useState(false); // fenêtre d'abonnement ouverte pour débloquer l'essai
    const LAST_ACTIVE_KEY = 'monquiz_last_active_thematique';
    const [activeSlug, setActiveSlugRaw] = useState(() => {
        if (initialActiveSlug) return initialActiveSlug;
        // Fallback 1: dedicated localStorage key (most reliable)
        try {
            const direct = localStorage.getItem(LAST_ACTIVE_KEY);
            if (direct) return direct;
        } catch {}
        // Fallback 2: prefs in localStorage
        try {
            const stored = JSON.parse(localStorage.getItem('monquiz_dashboard_prefs') || '{}');
            return stored.lastActiveThematique || null;
        } catch { return null; }
    });
    const setActiveSlug = (slug) => {
        setActiveSlugRaw(slug);
        if (slug) {
            try { localStorage.setItem(LAST_ACTIVE_KEY, slug); } catch {}
        }
        if (onActiveSlugChange && slug) onActiveSlugChange(slug);
        setInlineTab(prefs.lastTab?.[slug] || 'quiz');
    };
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingLabel, setEditingLabel] = useState('');
    const [editingDesc, setEditingDesc] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);
    // Panels (codes / publication / édition) — persisted per thematique slug
    // so the same panel re-opens after a reload or when switching back to the
    // same thematique.
    const PANELS_KEY = 'monquiz_hub_panels';
    const readPanelsMap = () => {
        try { return JSON.parse(localStorage.getItem(PANELS_KEY) || '{}'); } catch { return {}; }
    };
    const initialPanels = (() => {
        const map = readPanelsMap();
        const slug = initialActiveSlug || (typeof localStorage !== 'undefined' ? localStorage.getItem(LAST_ACTIVE_KEY) : null);
        return (slug && map[slug]) || { share: false, publish: false, settings: false, gestion: false };
    })();
    const [showSettings, setShowSettings] = useState(initialPanels.settings);
    const [showGestion, setShowGestion] = useState(initialPanels.gestion);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [showPublish, setShowPublish] = useState(initialPanels.publish);
    const [showShare, setShowShare] = useState(initialPanels.share);
    const panelRef = useRef(null);
    const [showSubModal, setShowSubModal] = useState(false);
    const [msg, setMsg] = useState(null);
    const [inlineTab, setInlineTab] = useState(() => {
        if (prefs.lastTab?.[initialActiveSlug]) return prefs.lastTab[initialActiveSlug];
        // Fallback: read active slug from localStorage and get its tab
        try {
            const savedSlug = localStorage.getItem(LAST_ACTIVE_KEY);
            if (savedSlug && prefs.lastTab?.[savedSlug]) return prefs.lastTab[savedSlug];
            const stored = JSON.parse(localStorage.getItem('monquiz_dashboard_prefs') || '{}');
            if (savedSlug && stored.lastTab?.[savedSlug]) return stored.lastTab[savedSlug];
        } catch {}
        return 'quiz';
    });
    const [searchParams, setSearchParams] = useSearchParams();

    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        fetchThematiques();
        api.get('/custom/thematiques.php').then(({ data }) => {
            if (data.success) {
                setPricing(data.data.pricing || null);
                setUnusedSubs(data.data.unused_subscriptions || 0);
                setTrialOffer(data.data.trial_available
                    ? { nb_questions: data.data.trial_nb_questions || 20, days: data.data.trial_days || 7 }
                    : null);
            }
        }).catch(e => console.warn("Erreur:", e));
    }, []);

    // Restore panel open-state when switching thematique (per-slug).
    useEffect(() => {
        if (!activeSlug || activeSlug === '__onboarding') return;
        const map = readPanelsMap();
        const saved = map[activeSlug] || { share: false, publish: false, settings: false, gestion: false };
        setShowShare(!!saved.share);
        setShowPublish(!!saved.publish);
        setShowSettings(!!saved.settings);
        setShowGestion(!!saved.gestion);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSlug]);

    // Persist panel state whenever one changes.
    useEffect(() => {
        if (!activeSlug || activeSlug === '__onboarding') return;
        try {
            const map = readPanelsMap();
            map[activeSlug] = { share: showShare, publish: showPublish, settings: showSettings, gestion: showGestion };
            localStorage.setItem(PANELS_KEY, JSON.stringify(map));
        } catch {}
    }, [activeSlug, showShare, showPublish, showSettings, showGestion]);

    useEffect(() => {
        if (thematiques.length > 0 && !thematiquesLoading) {
            const allThematiques = [...thematiques, ...sharedThematiques];
            // Highest priority : URL param ?thematique=<slug> (ex. lien depuis email rappel session)
            const urlTarget = searchParams.get('thematique');
            if (urlTarget && allThematiques.some(t => t.slug === urlTarget)) {
                setActiveSlug(urlTarget);
                // Scroll auto si hash #reprise-rapide présent (lien depuis email session pause)
                const wantsScroll = window.location.hash === '#reprise-rapide';
                // Nettoie le param pour ne pas le rejouer sur un refresh ou un retour navigateur
                searchParams.delete('thematique');
                setSearchParams(searchParams, { replace: true });
                if (wantsScroll) {
                    // Délai pour laisser React rendre TabQuiz et son widget Reprise rapide
                    setTimeout(() => {
                        const el = document.getElementById('reprise-rapide');
                        if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        // Nettoie le hash de l'URL
                        history.replaceState(null, '', window.location.pathname + window.location.search);
                    }, 400);
                }
                return;
            }
            // Restore saved active thématique or pick the first accessible one
            if (activeSlug && allThematiques.some(t => t.slug === activeSlug)) return; // already valid
            // Try all sources: initial prop, dedicated localStorage, prefs
            const candidates = [initialActiveSlug, localStorage.getItem(LAST_ACTIVE_KEY)].filter(Boolean);
            const saved = candidates.reduce((found, slug) => found || allThematiques.find(t => t.slug === slug), null);
            const target = saved || thematiques.find(t => t.has_access) || thematiques[0];
            if (target) setActiveSlug(target.slug);
        }
    }, [thematiques, thematiquesLoading, sharedThematiques]);

    // Notify parent of active thématique change (for header override)
    useEffect(() => {
        if (onActiveChange) {
            const active = thematiques.find(t => t.slug === activeSlug);
            onActiveChange(active || null);
        }
    }, [activeSlug, thematiques]);

    // Detect payment return
    useEffect(() => {
        const payment = searchParams.get('payment');
        if (payment === 'success') {
            // Achat depuis une thématique d'essai : l'abonnement lui est rattaché côté serveur
            // (thematiques.php GET) → on y revient au lieu de l'écran de création.
            let trialSlug = null;
            try { trialSlug = sessionStorage.getItem('trial_unlock_slug'); sessionStorage.removeItem('trial_unlock_slug'); } catch {}
            setActiveSlug(trialSlug || '__onboarding');
            searchParams.delete('payment');
            setSearchParams(searchParams, { replace: true });
        }
    }, []);

    useEffect(() => {
        const pending = sessionStorage.getItem('pending_thematique_label');
        if (pending && unusedSubs > 0 && !thematiquesLoading) {
            sessionStorage.removeItem('pending_thematique_label');
            setNewName(pending);
            setActiveSlug('__onboarding');
        }
    }, [unusedSubs, thematiquesLoading]);

    const formatPrice = (p) => p ? `${parseFloat(p).toFixed(2).replace('.', ',')} \u20ac` : '\u2014';

    // « Débloquer la thématique » (essai) : fenêtre d'abonnement ; au retour du paiement,
    // l'abonnement est rattaché à la thématique d'essai et on la rouvre.
    const openUnlock = (slug) => {
        try { if (slug) sessionStorage.setItem('trial_unlock_slug', slug); } catch {}
        setUnlockMode(true);
        setShowSubModal(true);
    };
    const handleTrialStarted = ({ thematiqueId, slug, jobId }) => {
        setTrialOffer(null);
        if (jobId) useCustomStore.getState().startPolling(jobId, thematiqueId);
        fetchThematiques().then(() => { if (slug) setActiveSlug(slug); });
    };

    // Sort thematiques by user-defined order
    const customOrder = prefs.customThematiqueOrder || [];
    const sortedThematiques = [...thematiques].sort((a, b) => {
        const ia = customOrder.indexOf(a.slug);
        const ib = customOrder.indexOf(b.slug);
        if (ia === -1 && ib === -1) return 0;
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
    });

    const activeThematique = sortedThematiques.find(t => t.slug === activeSlug)
        || sharedThematiques.find(t => t.slug === activeSlug);
    // Actions propriétaire (personnaliser / gestion / partage / publication) : uniquement si
    // la thématique active est POSSÉDÉE — pas un partage read-only reçu via code.
    const activeIsOwned = sortedThematiques.some(t => t.slug === activeSlug);
    // Thématique d'essai non convertie : fonctions réservées à l'abonnement visibles mais verrouillées.
    const activeTrial = activeIsOwned ? (activeThematique?.trial || null) : null;
    const trialLockedBtn = (label) => (
        <button
            className="mt-hub__header-action-btn mt-hub__header-action-btn--labeled mt-hub__header-action-btn--trial-locked"
            onClick={() => openUnlock(activeSlug)}
            title="Disponible avec l'abonnement à la thématique"
        >
            <Lock size={14} />
            <span>{label}</span>
        </button>
    );

    // Drag & drop handlers for thematique pills
    const handleThemDragStart = (e, idx) => {
        dragIdxRef.current = idx;
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleThemDragOver = (e) => e.preventDefault();
    const handleThemDrop = (e, dropIdx) => {
        e.preventDefault();
        const dragIdx = dragIdxRef.current;
        if (dragIdx === null || dragIdx === dropIdx) return;
        const reordered = [...sortedThematiques];
        const [moved] = reordered.splice(dragIdx, 1);
        reordered.splice(dropIdx, 0, moved);
        setCustomThematiqueOrder(reordered.map(t => t.slug));
        dragIdxRef.current = null;
    };

    const inlineContentRef = useRef(null);
    const handleInlineTabChange = (tab) => {
        setInlineTab(tab);
        if (activeSlug) setTabForScope(activeSlug, tab);
        // Scroll fluide vers la cible précise du contenu :
        //   - 'quiz'      → .arc-nav (classe historique du dock de modes)
        //   - 'resultats' → .sl-resultats (la section des résultats)
        // Délai pour laisser le re-render React poser les éléments dans le DOM.
        const targetSelector = tab === 'resultats' ? '.sl-resultats' : '.arc-nav';
        setTimeout(() => {
            const root = inlineContentRef.current;
            const target = root?.querySelector(targetSelector) || root;
            target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 80);
    };

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        try {
            if (isAdmin) {
                const result = await createThematique(newName.trim(), '');
                setNewName('');
                if (result?.slug) setActiveSlug(result.slug);
                setCreating(false); return;
            }
            if (unusedSubs <= 0) {
                sessionStorage.setItem('pending_thematique_label', newName.trim());
                setShowSubModal(true);
                setCreating(false);
                return;
            }
            const { data: subData } = await api.get('/user/purchases.php');
            const subs = (subData.data?.purchases || []).filter(s =>
                s.scope_slug === 'thematique-personnalisable' && s.statut === 'actif'
            );
            const usedSubIds = new Set(thematiques.map(t => t.subscription_id).filter(Boolean));
            const unusedSub = subs.find(s => !usedSubIds.has(s.id));
            if (!unusedSub) { setMsg({ type: 'error', text: 'Aucun abonnement disponible.' }); setCreating(false); return; }
            const result = await createThematique(newName.trim(), '', unusedSub.id);
            setNewName(''); setUnusedSubs(prev => prev - 1);
            if (result?.slug) setActiveSlug(result.slug);
        } catch (e) { setMsg({ type: 'error', text: e.message }); }
        setCreating(false);
    };

    const handleSaveEdit = async (id) => {
        if (!editingLabel.trim()) { setEditingId(null); return; }
        const updates = { label: editingLabel.trim() };
        if (editingDesc !== (activeThematique?.description || '')) {
            updates.description = editingDesc.trim();
        }
        await updateThematique(id, updates);
        setEditingId(null);
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        const deletedSlug = thematiques.find(t => t.id === confirmDelete.id)?.slug;
        await deleteThematique(confirmDelete.id);
        setConfirmDelete(null);
        if (activeSlug === deletedSlug) setActiveSlug(null);
    };

    const handleColorChange = async (color) => {
        if (!activeThematique) return;
        await updateThematique(activeThematique.id, { couleur_theme: color });
        fetchThematiques();
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !activeThematique) return;
        const allowedMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
        if (file.type && !allowedMimes.includes(file.type)) {
            setMsg({ type: 'error', text: 'Format non supporté (JPG, PNG, WebP, GIF ou AVIF)' });
            e.target.value = '';
            return;
        }
        setUploadingImage(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            formData.append('thematique_id', String(activeThematique.id));
            const { data } = await api.post('/custom/thematiques.php?action=upload_image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (data.success) {
                fetchThematiques();
                setMsg({ type: 'success', text: 'Image mise à jour' });
            }
        } catch (err) {
            const serverMsg = err?.response?.data?.error;
            setMsg({ type: 'error', text: serverMsg || 'Erreur upload image' });
        }
        setUploadingImage(false);
        e.target.value = '';
    };

    const hasAnyThematique = thematiques.length > 0;
    const showOnboarding = activeSlug === '__onboarding';

    // Default to onboarding when no thématiques
    useEffect(() => {
        if (!thematiquesLoading && !hasAnyThematique && !isAdmin && activeSlug !== '__onboarding') {
            setActiveSlug('__onboarding');
        }
    }, [thematiquesLoading, hasAnyThematique, isAdmin]);

    // Flèches de scroll de la sub-nav (même patron que le bandeau de scopes du Header)
    const hubNavRef = useRef(null);
    const [hubNavScroll, setHubNavScroll] = useState({ left: false, right: false });
    const checkHubNavScroll = () => {
        const el = hubNavRef.current;
        if (!el) return;
        setHubNavScroll({
            left: el.scrollLeft > 4,
            right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
        });
    };
    useEffect(() => {
        checkHubNavScroll();
        window.addEventListener('resize', checkHubNavScroll);
        return () => window.removeEventListener('resize', checkHubNavScroll);
    }, [thematiquesLoading, thematiques.length]);
    const scrollHubNav = (dir) => {
        const el = hubNavRef.current;
        el?.scrollBy({ left: dir * Math.round((el.clientWidth || 400) * 0.8), behavior: 'smooth' });
    };

    // === HUB ===
    return (
        <div className="mt-hub">
            {msg && <div className={`admin-q-msg admin-q-msg--${msg.type}`} onClick={() => setMsg(null)}>{msg.text}</div>}

            {/* Sub-nav: scope pills */}
            {hasAnyThematique && <div className="mt-hub__nav-wrap">
            {hubNavScroll.left && (
                <button className="mt-hub__nav-arrow" onClick={() => scrollHubNav(-1)} aria-label="Faire défiler vers la gauche">
                    <ChevronLeft size={16} />
                </button>
            )}
            <div className="mt-hub__nav" ref={hubNavRef} onScroll={checkHubNavScroll}>
                {thematiquesLoading ? <Loader2 size={16} className="spin" /> : (
                    <>
                        {sortedThematiques.map((t, tIdx) => {
                            const bgImage = t.image || hubImage;
                            const color = t.couleur_theme || hubColor || '#8B5CF6';
                            const isActive = activeSlug === t.slug;
                            return (
                                <div
                                    key={t.id}
                                    className="mt-hub__scope-card-wrap"
                                    draggable
                                    onDragStart={e => handleThemDragStart(e, tIdx)}
                                    onDragOver={handleThemDragOver}
                                    onDrop={e => handleThemDrop(e, tIdx)}
                                >
                                    <button
                                        className={`mt-hub__scope-card ${isActive ? 'mt-hub__scope-card--active' : ''} ${!t.has_access ? 'mt-hub__scope-card--expired' : ''} ${bgImage ? 'mt-hub__scope-card--bg' : ''}`}
                                        onClick={() => { setActiveSlug(t.slug); setShowSettings(false); }}
                                        style={{
                                            ...(bgImage ? { backgroundImage: `url(${bgImage})` } : {}),
                                            '--scope-color': color,
                                        }}
                                    >
                                        <span className="mt-hub__scope-card-label">
                                            {t.label.replace(/^Ma Thematique — /, '')}
                                        </span>
                                        {!t.has_access && <Lock size={12} className="mt-hub__scope-card-lock" />}
                                    </button>
                                    {isActive && (
                                        <button
                                            className="mt-hub__scope-card-edit"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowSettings(!showSettings);
                                                if (!showSettings) {
                                                    setEditingLabel(t.label.replace(/^Ma Thematique — /, ''));
                                                    setEditingDesc(t.description || '');
                                                    setEditingId(t.id);
                                                } else {
                                                    setEditingId(null);
                                                }
                                            }}
                                            title="Personnaliser (nom, couleur, image)"
                                        >
                                            <Edit3 size={12} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}

                        {/* Thématiques PARTAGÉES (read-only) : volontairement PAS dans le hub.
                            Logique : read-only = bandeau scope du haut, éditable = Mes Thématiques.
                            Elles restent accessibles via le bandeau du haut (vue autonome read-only). */}

                        <button
                            className={`mt-hub__scope-card mt-hub__scope-card--add ${showOnboarding ? 'mt-hub__scope-card--active' : ''}`}
                            onClick={() => { setActiveSlug('__onboarding'); setShowSettings(false); }}
                            style={{ '--scope-color': '#8B5CF6' }}
                        >
                            <Plus size={20} strokeWidth={1.5} />
                        </button>

                        {/* Edit pencil is on the active card itself — see scope card render */}
                    </>
                )}
            </div>
            {hubNavScroll.right && (
                <button className="mt-hub__nav-arrow" onClick={() => scrollHubNav(1)} aria-label="Faire défiler vers la droite">
                    <ChevronRight size={16} />
                </button>
            )}
            </div>}

            {/* Header slot — buttons labellés positionnables par le parent.
                Le parent reçoit un objet { personnaliser, gestion, partager, publier }
                pour placer chaque bouton là où c'est pertinent (ex: personnaliser en
                colonne gauche sous le titre, les autres en colonne droite). */}
            {headerSlot && headerSlot(activeThematique, activeThematique && {
                personnaliser: activeTrial ? trialLockedBtn("Personnaliser l'affichage") : activeIsOwned && activeThematique.has_access ? (
                    <button
                        className={`mt-hub__header-action-btn mt-hub__header-action-btn--labeled ${showSettings ? 'mt-hub__header-action-btn--active' : ''}`}
                        onClick={() => {
                            const next = !showSettings;
                            setShowSettings(next);
                            setShowShare(false);
                            setShowPublish(false);
                            setShowGestion(false);
                            if (next && activeThematique) {
                                setEditingLabel(activeThematique.label.replace(/^Ma Thematique — /, ''));
                                setEditingDesc(activeThematique.description || '');
                                setEditingId(activeThematique.id);
                            } else {
                                setEditingId(null);
                            }
                        }}
                        title="Personnaliser le nom, la couleur, l'image"
                    >
                        <Edit3 size={14} />
                        <span>Personnaliser l'affichage</span>
                    </button>
                ) : null,
                gestion: activeIsOwned && (activeThematique.has_access || activeTrial) ? (
                    <button
                        className={`mt-hub__header-action-btn mt-hub__header-action-btn--labeled ${showGestion ? 'mt-hub__header-action-btn--active' : ''}`}
                        onClick={() => { setShowGestion(g => !g); setShowShare(false); setShowPublish(false); setShowSettings(false); }}
                        title="Upload de documents et génération IA"
                    >
                        <Settings size={14} />
                        <span>Gestion des sujets</span>
                    </button>
                ) : null,
                partager: activeTrial ? trialLockedBtn('Gérer les partages') : activeIsOwned && activeThematique.has_access ? (
                    <button
                        className={`mt-hub__header-action-btn mt-hub__header-action-btn--labeled ${showShare ? 'mt-hub__header-action-btn--active' : ''}`}
                        onClick={() => { setShowShare(s => !s); setShowPublish(false); setShowSettings(false); setShowGestion(false); }}
                        title="Générer des codes d'accès pour partager"
                    >
                        <Share2 size={14} />
                        <span>Gérer les partages</span>
                    </button>
                ) : null,
                /* Publier reste disponible sans abonnement actif : un user peut publier
                   sa thématique et recevoir un abonnement offert par l'équipe en contrepartie. */
                publier: activeTrial ? trialLockedBtn('Publier') : activeIsOwned ? (
                    <button
                        className={`mt-hub__header-action-btn mt-hub__header-action-btn--labeled ${showPublish ? 'mt-hub__header-action-btn--active' : ''}`}
                        onClick={() => { setShowPublish(s => !s); setShowShare(false); setShowSettings(false); setShowGestion(false); }}
                        title="Soumettre votre thématique à publication"
                    >
                        <Send size={14} />
                        <span>Gérer les publications</span>
                    </button>
                ) : null,
            }, activeSlug && !showOnboarding && user && (
                <div className="sl-inline-hist">
                    <button
                        className={inlineTab !== 'resultats' ? 'sl-inline-hist--active' : ''}
                        onClick={() => handleInlineTabChange('quiz')}
                    >
                        S’entraîner
                    </button>
                    <button
                        className={inlineTab === 'resultats' ? 'sl-inline-hist--active' : ''}
                        onClick={() => handleInlineTabChange('resultats')}
                    >
                        Mes résultats
                    </button>
                </div>
            ))}

            {/* === MODAL : Personnaliser la thématique === */}
            <BaseModal
                open={!!(showSettings && activeThematique)}
                onClose={() => { setShowSettings(false); setEditingId(null); }}
                title="Personnaliser la thématique"
                icon={<Edit3 size={16} />}
                maxWidth="720px"
                headerActions={
                    <button className="btn btn-sm btn-primary" onClick={() => { handleSaveEdit(editingId); setShowSettings(false); }} title="Sauvegarder et fermer">
                        <Save size={14} /> Sauvegarder
                    </button>
                }
            >
                {activeThematique && (
                    <div className="mt-hub__edit-fields">
                        <div className="mt-hub__edit-section">
                            <label>Nom</label>
                            <div className="mt-hub__edit-rename">
                                <span className="mt-hub__edit-prefix">Ma Thématique —</span>
                                <input
                                    value={editingLabel}
                                    onChange={(e) => setEditingLabel(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Escape') { setShowSettings(false); setEditingId(null); } }}
                                    placeholder="Titre..."
                                />
                            </div>
                        </div>
                        <div className="mt-hub__edit-section">
                            <label>Description</label>
                            <textarea
                                className="mt-hub__edit-desc"
                                value={editingDesc}
                                onChange={(e) => setEditingDesc(e.target.value)}
                                placeholder="Décrivez votre thématique..."
                                rows={2}
                            />
                        </div>
                        <div className="mt-hub__edit-section">
                            <label>Couleur</label>
                            <div className="mt-hub__color-grid">
                                {PRESET_COLORS.map(c => (
                                    <button key={c}
                                        className={`mt-hub__color-swatch ${activeThematique.couleur_theme === c ? 'mt-hub__color-swatch--active' : ''}`}
                                        style={{ background: c }}
                                        onClick={() => handleColorChange(c)}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="mt-hub__edit-section">
                            <label>Image</label>
                            <div className="mt-hub__image-upload">
                                {(activeThematique.image || hubImage) && (
                                    <img src={activeThematique.image || hubImage} alt=""
                                        className={`mt-hub__image-preview ${!activeThematique.image ? 'mt-hub__image-preview--inherited' : ''}`} />
                                )}
                                <label className="btn btn-xs btn-secondary mt-hub__image-btn">
                                    {uploadingImage ? <Loader2 size={12} className="spin" /> : <Upload size={12} />}
                                    {activeThematique.image ? 'Changer' : 'Personnaliser'}
                                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif,.jpg,.jpeg,.png,.webp,.gif,.avif" onChange={handleImageUpload} hidden />
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </BaseModal>

            {/* === MODAL : Publier la thématique === */}
            <BaseModal
                open={!!(showPublish && activeThematique)}
                onClose={() => setShowPublish(false)}
                title={activeThematique ? `Publier « ${activeThematique.label.replace(/^Ma Thematique — /, '')} »` : 'Publier'}
                icon={<Send size={16} />}
                maxWidth="720px"
                variant="success"
            >
                {activeThematique && <PublicationStatus thematiqueId={activeThematique.id} />}
                {activeThematique && (
                    <PublishForm
                        thematiqueId={activeThematique.id}
                        thematiqueLabel={activeThematique.label.replace(/^Ma Thematique — /, '')}
                        nbSousSujets={activeThematique.nb_sous_sujets || 0}
                        nbQuestions={activeThematique.nb_questions || 0}
                        onPublished={() => { setShowPublish(false); fetchThematiques(); }}
                    />
                )}
            </BaseModal>

            {/* === MODAL : Partager la thématique === */}
            <BaseModal
                open={!!(showShare && activeThematique)}
                onClose={() => setShowShare(false)}
                title={activeThematique ? `Partager « ${activeThematique.label.replace(/^Ma Thematique — /, '')} »` : 'Partager'}
                icon={<Share2 size={16} />}
                maxWidth="min(95vw, 1300px)"
            >
                {activeThematique && (
                    <ShareCodesPanel
                        thematiqueId={activeThematique.id}
                        thematiqueLabel={activeThematique.label.replace(/^Ma Thematique — /, '')}
                        onClose={() => setShowShare(false)}
                    />
                )}
            </BaseModal>

            {/* Inline ScopeLanding */}
            {activeSlug && !showOnboarding && (
                <div ref={inlineContentRef} style={{ scrollMarginTop: 80 }}>
                    <ScopeLandingInline
                        key={activeSlug}
                        scopeSlug={activeSlug}
                        fallbackImage={hubImage}
                        activeTab={inlineTab}
                        onTabChange={handleInlineTabChange}
                        gestionOpen={showGestion}
                        onOpenGestion={() => { setShowGestion(true); setShowShare(false); setShowPublish(false); setShowSettings(false); }}
                        onCloseGestion={() => setShowGestion(false)}
                        onUnlock={activeTrial ? () => openUnlock(activeSlug) : null}
                    />
                </div>
            )}

            {/* Onboarding — présentation + création */}
            {showOnboarding && trialOffer && (
                <>
                    <TrialStartCard
                        nbQuestions={trialOffer.nb_questions}
                        days={trialOffer.days}
                        onStarted={handleTrialStarted}
                    />
                    <p className="trial-card__or">Vous préférez vous abonner directement ? Le détail de l’offre est ci-dessous.</p>
                </>
            )}
            {showOnboarding && (
                <div className="mt-presentation">
                    <h2 className="mt-presentation__title"><ContentIllustration name="sparkles" size={48} /> Créez vos QCM depuis vos documents</h2>
                    <div className="mt-presentation__steps">
                        <div className="mt-presentation__step">
                            <span className="mt-presentation__step-num">1</span>
                            <div>
                                <strong>Uploadez vos documents</strong>
                                <p>PDF, Word, texte, images — jusqu'à 10 Mo par fichier. <strong>30 documents inclus</strong> par thématique, packs supplémentaires disponibles.</p>
                            </div>
                        </div>
                        <div className="mt-presentation__step">
                            <span className="mt-presentation__step-num">2</span>
                            <div>
                                <strong>L'IA génère vos QCM</strong>
                                <p>De 10 à 50 questions par QCM. Quota inclus : 30 générations IA + 100 questions IA, extensible sans limite via Pack IA.</p>
                            </div>
                        </div>
                        <div className="mt-presentation__step">
                            <span className="mt-presentation__step-num">3</span>
                            <div>
                                <strong>Révisez et progressez</strong>
                                <p>Quiz classique, apprentissage, mélange, progression intelligente — les mêmes outils que les thématiques éditoriales.</p>
                            </div>
                        </div>
                        <div className="mt-presentation__step">
                            <span className="mt-presentation__step-num">4</span>
                            <div>
                                <strong>Partagez avec un proche</strong>
                                <p><strong>1 code de partage offert</strong> avec chaque abonnement. Besoin de plus ? Des packs de codes supplémentaires sont disponibles.</p>
                            </div>
                        </div>
                    </div>

                    {/* Création / achat */}
                    <div className="mt-presentation__action">
                        {(isAdmin || unusedSubs > 0) ? (
                            <div className="mt-presentation__create-form">
                                <input
                                    type="text"
                                    placeholder="Nom de votre thématique..."
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                    maxLength={100}
                                    autoFocus
                                />
                                <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !newName.trim()}>
                                    {creating ? <Loader2 size={16} className="spin" /> : <><Plus size={16} /> Créer ma thématique</>}
                                </button>
                            </div>
                        ) : (
                            <>
                                {pricing && (
                                    <p className="mt-presentation__pricing">
                                        À partir de {formatPrice(pricing.price_1m)}/mois
                                    </p>
                                )}
                                <div className="mt-presentation__create-form">
                                    <input
                                        type="text"
                                        placeholder="Nom de votre thématique..."
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        maxLength={100}
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            if (newName.trim()) sessionStorage.setItem('pending_thematique_label', newName.trim());
                                            setShowSubModal(true);
                                        }}
                                    >
                                        <CreditCard size={16} /> {hasAnyThematique ? 'Nouvelle thématique' : 'Créer ma première thématique'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {!activeSlug && !thematiquesLoading && !showOnboarding && (
                <div className="mt-hub__empty">
                    <ContentIllustration name="brain" size={64} />
                    <p>Sélectionnez ou créez une thématique ci-dessus</p>
                </div>
            )}

            {showSubModal && pricing && (
                <SubscriptionModal
                    scopeId={pricing.scope_id}
                    scopeLabel="Thématique personnalisable"
                    onClose={() => { setShowSubModal(false); setUnlockMode(false); }}
                    source={unlockMode ? 'essai_perso' : 'creer_perso'}
                    ctaLabel={unlockMode ? 'Débloquer ma thématique' : 'Créer ma thématique'}
                    subtitle={<>
                        {unlockMode && <p style={{ margin: '0 0 8px' }}><strong>Votre sujet d'essai et votre progression sont conservés.</strong></p>}
                        <strong>Pendant la durée de l'abonnement :</strong>
                        <ul style={{ textAlign: 'left', margin: '8px auto 0', maxWidth: '420px', paddingLeft: '20px', listStyle: 'disc' }}>
                            <li><strong>30 générations IA</strong> (création de sujet ou enrichissement, document ou prompt) + <strong>100 questions IA</strong> inclus par thématique</li>
                            <li>Génération de 10 à 50 questions par QCM · jusqu'à 50 questions par sujet</li>
                            <li>Extension à la demande : Packs Booster cumulables sans limite (+30 générations IA, +100 questions IA à partir de 10 €)</li>
                            <li>Accès illimité à vos QCM générés et aux fonctionnalités avancées de MonQuiz.fr</li>
                        </ul>
                    </>}
                    pricing={pricing}
                />
            )}

            <ConfirmDialog open={!!confirmDelete}
                title="Supprimer la thématique"
                message={confirmDelete ? <>Supprimer <strong>{confirmDelete.label}</strong> et tout son contenu ?</> : ''}
                confirmLabel="Supprimer" variant="danger"
                onConfirm={handleDelete} onCancel={() => setConfirmDelete(null)} />

        </div>
    );
}
